import { FC, useMemo, useState } from 'react'
import { Bell, Plug, Globe, Building2 } from 'lucide-react'
import { useAuth } from '../../modules/auth'
import { usePermissions } from '../../modules/auth/hooks/usePermissions'
import { PageCard, SectionLabel, TOKENS } from '../../components/design-system'
import { RulesList } from './components/RulesList'
import { WhatsAppConnect } from './components/WhatsAppConnect'

type Tab = 'rules-company' | 'rules-global' | 'integrations'

export const NotificationsPage: FC = () => {
  const { currentUser } = useAuth()
  const perms = usePermissions()
  const companyId = currentUser?.last_company_id ? +currentUser.last_company_id : undefined

  // Per-tab gating reads off the snapshot returned by /me/permissions.
  // The current company must also have the notifications feature
  // enabled (platform-level opt-in) for any company-scoped tab to
  // appear. Global rules require can_manage_notifications regardless
  // of the current company's flag (global rules apply across enabled
  // companies). WhatsApp requires can_manage_whatsapp AND the
  // current company's flag.
  const companyPerm = perms.permFor(companyId)
  const companyEnabled = !!companyPerm?.company_notifications_enabled
  const companyPremium = !!companyPerm?.company_notifications_premium
  const canEditCompanyRules = !!companyPerm && companyEnabled && (companyPerm.is_admin || companyPerm.is_manager)
  const canEditGlobalRules = perms.can_manage_notifications
  const canEditWhatsApp = !!companyPerm && companyEnabled && companyPerm.can_manage_whatsapp

  const availableTabs: Tab[] = useMemo(() => {
    const t: Tab[] = []
    if (canEditCompanyRules) t.push('rules-company')
    if (canEditGlobalRules) t.push('rules-global')
    if (canEditWhatsApp) t.push('integrations')
    return t
  }, [canEditCompanyRules, canEditGlobalRules, canEditWhatsApp])

  const [tab, setTab] = useState<Tab>(availableTabs[0] ?? 'rules-company')

  const TabButton: FC<{ value: Tab; icon: JSX.Element; label: string; disabled?: boolean; reason?: string }> = ({ value, icon, label, disabled, reason }) => {
    const active = tab === value
    if (disabled) {
      return (
        <button
          type='button'
          title={reason}
          disabled
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 14px', fontSize: 13, fontWeight: 600,
            border: 'none', background: 'transparent',
            color: TOKENS.color.divider,
            borderBottom: '2px solid transparent',
            cursor: 'not-allowed',
          }}
        >{icon} {label}</button>
      )
    }
    return (
      <button
        type='button'
        onClick={() => setTab(value)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '8px 14px', fontSize: 13, fontWeight: 600,
          border: 'none', background: 'transparent',
          color: active ? TOKENS.color.action : TOKENS.color.muted,
          borderBottom: `2px solid ${active ? TOKENS.color.action : 'transparent'}`,
          cursor: 'pointer',
        }}
      >{icon} {label}</button>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: TOKENS.spacing.md }}>
      <PageCard>
        <SectionLabel>Automação</SectionLabel>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
          <Bell size={20} color={TOKENS.color.action} />
          <h1 style={{ fontSize: 22, fontWeight: 700, color: TOKENS.color.text, margin: 0 }}>Notificações</h1>
        </div>
        <p style={{ marginTop: 8, fontSize: 13, color: TOKENS.color.muted, lineHeight: 1.5, marginBottom: 0 }}>
          Configure regras que disparam notificações por email ou WhatsApp quando eventos
          relevantes acontecem nas suas caixas. Regras por empresa sobrescrevem as globais.
        </p>
      </PageCard>

      <div style={{ display: 'flex', gap: TOKENS.spacing.md, borderBottom: `1px solid ${TOKENS.color.border}`, paddingLeft: 4 }}>
        <TabButton value='rules-company' icon={<Building2 size={14} />} label='Regras da empresa'
          disabled={!canEditCompanyRules}
          reason={companyPerm && !companyEnabled
            ? 'Notificações estão desativadas para esta empresa. Peça ao admin da plataforma para liberar.'
            : 'Precisa ser admin ou gerente da empresa selecionada.'} />
        <TabButton value='rules-global' icon={<Globe size={14} />} label='Regras globais'
          disabled={!canEditGlobalRules}
          reason='Precisa ter a permissão "Gerenciar notificações globais" no seu perfil de administrador.' />
        <TabButton value='integrations' icon={<Plug size={14} />} label='WhatsApp'
          disabled={!canEditWhatsApp}
          reason={companyPerm && !companyEnabled
            ? 'Notificações estão desativadas para esta empresa. Peça ao admin da plataforma para liberar.'
            : 'Precisa ter a permissão "Gerenciar WhatsApp" no seu perfil de administrador.'} />
      </div>

      {availableTabs.length === 0 && (
        <PageCard>
          <div style={{ color: TOKENS.color.muted, fontSize: 13 }}>
            Seu perfil não tem permissões para gerenciar notificações.
            Peça ao administrador para habilitar uma das permissões no seu cadastro.
          </div>
        </PageCard>
      )}

      {tab === 'rules-company' && canEditCompanyRules && companyId && (
        <RulesList scope='company' companyId={companyId} premiumEnabled={companyPremium} />
      )}
      {/* Global rules always show reminder controls — global is an
          admin-level abstraction and platform admins are trusted with
          the cadence; backend doesn't gate global by premium. */}
      {tab === 'rules-global' && canEditGlobalRules && <RulesList scope='global' premiumEnabled />}
      {tab === 'integrations' && canEditWhatsApp && companyId && <WhatsAppConnect companyId={companyId} />}
    </div>
  )
}

export default NotificationsPage
